import React from 'react';
import { PublicSection, PublicCTAGroup, Reveal } from '@/components/public';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';

const FAQ_GROUPS = [
  {
    category: 'Getting Started',
    items: [
      {
        id: 'what-types-of-businesses-can-join',
        q: 'What types of businesses can join Hungry Canadian?',
        a: 'Hungry Canadian works with a growing range of independent businesses, including restaurants, cafés, bakeries, liquor stores, grocery stores, specialty food businesses, and other local retailers. As the platform evolves, additional business categories may become available.',
      },
      {
        id: 'how-do-i-get-started',
        q: 'How do I get started?',
        a: 'The first step is simply reaching out. We’ll learn about your business, discuss your goals, and determine whether Hungry Canadian is the right fit. If you decide to move forward, we’ll guide you through the onboarding process and prepare your online storefront.',
      },
      {
        id: 'how-long-does-onboarding-take',
        q: 'How long does onboarding take?',
        a: 'Every business is different. The timeline depends on factors such as the size of your menu or product catalogue, available business information, and readiness for launch. We’ll work with you throughout the process to help prepare your storefront as efficiently as possible.',
      },
      {
        id: 'do-i-need-technical-experience',
        q: 'Do I need technical experience?',
        a: 'No. We’ll guide you through the onboarding process and help prepare your storefront. Once your account is active, you’ll have access to the merchant tools needed to manage your business.',
      },
    ],
  },
  {
    category: 'Orders & Operations',
    items: [
      {
        id: 'can-i-offer-pickup-delivery-or-both',
        q: 'Can I offer pickup, delivery, or both?',
        a: 'Yes. Depending on your business and operating model, you can offer pickup, delivery, or both where available.',
      },
      {
        id: 'can-i-manage-my-own-products-or-menu',
        q: 'Can I manage my own products or menu?',
        a: 'Yes. Merchants have access to update products, menus, pricing, business hours, and other storefront information through their merchant dashboard.',
      },
      {
        id: 'can-i-temporarily-pause-online-ordering',
        q: 'Can I temporarily pause online ordering?',
        a: 'Yes. If your business needs to temporarily stop accepting online orders, your storefront can be adjusted accordingly.',
      },
      {
        id: 'can-i-continue-using-my-existing-systems',
        q: 'Can I continue using my existing systems?',
        a: 'Many businesses continue using their existing point-of-sale systems and operational workflows alongside Hungry Canadian. Integration options vary depending on your current technology and business requirements.',
      },
    ],
  },
  {
    category: 'Integrations & Hardware',
    items: [
      {
        id: 'which-pos-systems-do-you-integrate-with',
        q: 'Which POS systems do you integrate with?',
        a: 'Hungry Canadian offers direct POS integrations with leading systems including Square, Lightspeed, Clover, Shopify POS, EPOS Now, and Deliverect. Direct POS integration is available on the Pro Connect and Growth VIP packages, where our team provides guided POS mapping so your online orders flow straight into the system you already run your business on.',
      },
      {
        id: 'how-does-payment-processing-work',
        q: 'How does payment processing work?',
        a: 'Payments are handled securely through Stripe with automatic direct payouts to your account. Hungry Canadian charges 0% commission on your food sales—you keep what you earn. Standard third-party payment processing fees of 2.9% + $0.30 per transaction apply and are handled automatically, so there are no hidden software surprises.',
      },
      {
        id: 'do-you-support-thermal-receipt-printers',
        q: 'Do you support thermal receipt and kitchen ticket printers?',
        a: 'Yes. Platform-wide auto-printing for thermal kitchen tickets is included on every package. We support common thermal printer hardware such as Epson and Star Micronics, so incoming orders print automatically to your kitchen without any manual steps.',
      },
      {
        id: 'do-i-need-to-buy-new-hardware',
        q: 'Do I need to buy new hardware to get started?',
        a: 'In most cases, no. Many businesses launch using a tablet or existing devices, and Hungry Canadian works alongside common POS and thermal printer hardware you may already own. During onboarding we\u2019ll review your current setup and let you know if anything additional is recommended for your workflow.',
      },
    ],
  },
  {
    category: 'Customers',
    items: [
      {
        id: 'can-customers-order-directly-from-hungry-canadian',
        q: 'Can customers order directly from Hungry Canadian?',
        a: 'Yes. Customers can discover participating businesses through Hungry Canadian and place orders online using the platform.',
      },
      {
        id: 'can-customers-order-directly-from-my-business',
        q: 'Can customers order directly from my business?',
        a: 'Depending on your setup, customers may also be able to access your storefront directly through links or QR codes that point to your Hungry Canadian ordering page.',
      },
    ],
  },
  {
    category: 'Support',
    items: [
      {
        id: 'what-support-do-merchants-receive',
        q: 'What support do merchants receive?',
        a: 'Hungry Canadian provides onboarding guidance, platform assistance, and ongoing support to help businesses make the most of their online ordering experience.',
      },
      {
        id: 'do-you-help-after-launch',
        q: 'Do you help after launch?',
        a: 'Yes. Our relationship doesn’t end when your storefront goes live. We’re available to answer questions, provide guidance, and support businesses as they continue using the platform.',
      },
    ],
  },
  {
    category: 'Business Growth',
    items: [
      {
        id: 'can-hungry-canadian-support-promotions',
        q: 'Can Hungry Canadian support promotions and special offers?',
        a: 'Yes. Merchants can use available promotional tools and campaigns to help engage customers and support seasonal marketing initiatives.',
      },
      {
        id: 'can-i-update-my-business-information-later',
        q: 'Can I update my business information later?',
        a: 'Yes. Business information, operating hours, products, and other storefront details can be updated as your business evolves.',
      },
    ],
  },
  {
    category: 'Compliance',
    items: [
      {
        id: 'do-liquor-stores-have-different-requirements',
        q: 'Do liquor stores have different requirements?',
        a: 'Yes. Businesses selling regulated products must comply with all applicable laws and licensing requirements. Hungry Canadian supports merchants within those regulatory frameworks where permitted.',
      },
      {
        id: 'is-hungry-canadian-available-outside-alberta',
        q: 'Is Hungry Canadian available outside Alberta?',
        a: 'Hungry Canadian is currently focused on supporting Canadian communities, beginning in Alberta, with future expansion planned as the platform grows.',
      },
    ],
  },
  {
    category: 'Partnership',
    items: [
      {
        id: 'is-every-business-accepted',
        q: 'Is every business accepted?',
        a: 'Hungry Canadian works with businesses that align with the platform and its operational capabilities. Each inquiry begins with a conversation to ensure we’re a good fit for one another.',
      },
      {
        id: 'how-do-i-learn-more',
        q: 'How do I learn more?',
        a: 'Complete the contact form on this page and a member of the Hungry Canadian team will be in touch to discuss your business, answer your questions, and explain the next steps.',
      },
    ],
  },
];

const ALL_ITEMS = FAQ_GROUPS.flatMap((g) => g.items);

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: ALL_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
};

// Escape `<` so the serialized JSON can never break out of the <script> tag
// (defense-in-depth even though the content is fully static/controlled).
const FAQ_JSON_LD_STRING = JSON.stringify(FAQ_JSON_LD).replace(/</g, '\\u003c');

const scrollToForm = () =>
  document.getElementById('lead-form-anchor')?.scrollIntoView({ behavior: 'smooth' });

const STILL_QUESTIONS_CTA = {
  label: "Let's Talk",
  onClick: scrollToForm,
  testId: 'faq-lets-talk',
};

export default function FaqSection() {
  return (
    <PublicSection
      id="faq"
      testId="join-faq"
      variant="alt"
      eyebrow="Questions & answers"
      title="Frequently Asked Questions"
      description="Choosing an online ordering partner is an important decision. Here are answers to some of the questions business owners ask us most often."
    >
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: FAQ_JSON_LD_STRING }}
      />

      <div className="max-w-3xl mx-auto space-y-12">
        {FAQ_GROUPS.map((group, gi) => (
          <Reveal key={group.category} delay={gi * 0.03}>
            <div data-testid={`faq-group-${group.category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}>
              <div className="flex items-center gap-3 mb-3">
                <span aria-hidden className="h-3.5 w-1 bg-primary" />
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-primary">
                  {group.category}
                </p>
              </div>
              <Accordion type="single" collapsible className="w-full border-t border-border">
                {group.items.map((item) => (
                  <AccordionItem
                    key={item.id}
                    value={item.id}
                    id={item.id}
                    className="scroll-mt-28 border-b border-border"
                    data-testid={`faq-item-${item.id}`}
                  >
                    <AccordionTrigger
                      className="py-5 text-base md:text-lg font-heading font-bold tracking-tight text-foreground hover:no-underline data-[state=open]:text-primary"
                      data-testid={`faq-trigger-${item.id}`}
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent
                      className="pb-5 text-sm md:text-base text-muted-foreground leading-relaxed"
                      data-testid={`faq-answer-${item.id}`}
                    >
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.05}>
        <div
          className="mt-16 md:mt-20 max-w-2xl mx-auto text-center border-t border-border pt-14"
          data-testid="faq-closing"
        >
          <h3 className="text-3xl md:text-4xl font-heading font-black tracking-tight text-foreground">
            Still have questions?
          </h3>
          <p className="mt-3 text-base md:text-lg text-muted-foreground leading-relaxed">
            Every business is different, and we’re happy to discuss your specific
            goals, operations, and questions.
          </p>
          <div className="mt-7 flex justify-center">
            <PublicCTAGroup align="center" primary={STILL_QUESTIONS_CTA} />
          </div>
        </div>
      </Reveal>
    </PublicSection>
  );
}
